<template>
  <div class="blog">
    <div class="page-wrapper" v-if="entry && entry.typeHandle === 'blog'">
      <header class="blog__header c_wrapper c_wrapper--950">
        <Button
          v-if="entry.blogCategory && entry.blogCategory.length"
          class="blog__header__breadcrumb"
          unstyle
          reset
          :href="entry.blogCategory[0].uri"
          :label-text="entry.blogCategory[0].title"
        />
        <h1 class="blog__header__header">{{ entry.title }}</h1>
        <div class="blog__header__summary" v-if="entry.articleSummary" v-html="entry.articleSummary"></div>
        <div class="blog__header__author_date">
          <p class="blog__header__author_date__author" v-if="entry.authorName">
            {{ $t('By') + ' ' + entry.authorName }}
          </p>
          <p v-if="entry.entryPostDate">{{ entry.entryPostDate }}</p>
        </div>
        <SocialShare
          class="blog__social_share"
          :share-title="seomatic.title"
          :share-text="getSeomaticDescription ? getSeomaticDescription : ''"
          :share-url="entry.uri"
          v-if="seomatic.title && entry.uri"
        >
          <slot>
            <div class="blog__social_share__button">
              <span class="blog__social_share__button__text c_text--graphik_semibold">{{ $t('Share') }}</span>
              <IconSVG width="15" height="15" handle="new_window" aria-hidden="true" />
            </div>
          </slot>
          <div slot="fallback">
            <CopyToClipboard
              class="blog__social_share__copy_to_clipboard"
              copy-label="Copy URL to clipboard"
              :copy-text="getFullUrl"
            />
          </div>
        </SocialShare>
      </header>
      <div class="blog__block_matrix_wrapper">
        <MatrixBlockBlogPageContent :matrix-blocks="entry.blogContent" v-if="entry.blogContent" />
      </div>
      <MatrixBlockLandingPageContent
        class="blog__lp_matrix"
        :matrix-blocks="entry.landingPageContent"
        :related-posts-ids="relatedPostsIds"
        :entry-id="entry.id"
        v-if="entry.landingPageContent"
      />
    </div>
    <div class="page-wrapper" v-if="entry && entry.typeHandle === 'customerStory'">
      <Breadcrumb :links="breadcrumbLinks" />
      <MatrixBlockCustomerStoryContent
        v-if="entry.customerStoryContent"
        :matrix-blocks="entry.customerStoryContent"
        :read-more="entry.customerStoryReadMoreButton"
      />
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  reactive,
  toRefs,
  useContext,
  useFetch,
  useMeta,
  useRoute,
  useRouter,
  useStore,
} from '@nuxtjs/composition-api';
import { blogGql } from 'GQL/pages/blogGql.js';
import { gqlToObject } from 'JS/seomatic.js';
import { getFullUrl, log, selectedCategoriesFromCategoriesField } from 'JS/global.js';
import Button from 'Components/button/Button.vue';
import CopyToClipboard from 'Components/copy_to_clipboard/CopyToClipboard.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import MatrixBlockBlogPageContent from 'Components/matrix_blocks/MatrixBlockBlogPageContent.vue';
import MatrixBlockLandingPageContent from 'Components/matrix_blocks/MatrixBlockLandingPageContent.vue';
import MatrixBlockCustomerStoryContent from 'Components/matrix_blocks/MatrixBlockCustomerStoryContent.vue';
import SocialShare from 'Components/social_share/SocialShare.vue';
import Breadcrumb from 'Components/breadcrumb/Breadcrumb.vue';
export default defineComponent({
  name: 'PageBlog',
  head: {},
  components: {
    Button,
    CopyToClipboard,
    IconSVG,
    MatrixBlockBlogPageContent,
    MatrixBlockLandingPageContent,
    SocialShare,
    Breadcrumb,
    MatrixBlockCustomerStoryContent,
  },
  setup() {
    const { app, $craft, payload, $preview } = useContext();
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const state = reactive({
      entry: null,
      relatedPostsIds: [],
      seomatic: null,
    });

    useFetch(async () => {
      let data;
      if (!$preview && payload?.entry) {
        data = payload;
      } else {
        const request = await $craft({
          apiLog: 'PageBlog',
          query: blogGql,
          variables: {
            site: store.state.global.localeSites[app.i18n.locale].siteHandle,
            uri: `${route.value.params.contentSection + '/blog-post/' + route.value.params.slug}`,
          },
        });

        if (!request?.data?.entry) {
          router.replace('/error/404/');
          return null;
        }
        if (request?.data) {
          data = request.data;
        }
      }

      if (data?.entry) {
        state.entry = data.entry;

        if (data.entry.relatedProductCategories) {
          state.relatedPostsIds = selectedCategoriesFromCategoriesField(data.entry.relatedProductCategories);
        }
        if (data.entry.seomatic) {
          state.seomatic = data.entry.seomatic;
        }
        if (state.entry?.navigationTheme) {
          store.commit('global/updateGlobalDarkTheme', true);
        } else {
          store.commit('global/updateGlobalDarkTheme', false);
        }
      }
    });

    useMeta(() => {
      return state.seomatic ? { ...gqlToObject(state.seomatic) } : {};
    });

    return { ...toRefs(state) };
  },
  computed: {
    breadcrumbLinks() {
      const links = [{ label: this.$t('Home'), href: '/' }];

      if (this.entry && this.entry.typeHandle === 'customerStory') {
        if (this.entry.blogCategory) {
          this.entry.blogCategory.forEach((item) => {
            links.push({
              label: item.title,
              href: item.breadcrumbLink?.length ? item.breadcrumbLink[0]?.uri : item.uri,
            });
          });
        }
        links.push({ label: this.entry.title });
      }
      return links;
    },
    getSeomaticDescription() {
      return this.seomatic?.meta
        ? this.seomatic.meta
            .filter((obj) => {
              return obj.name === 'description';
            })
            .map((descriptionObj) => {
              return descriptionObj.content;
            })[0]
        : null;
    },
    getFullUrl,
  },
  mounted() {
    log('PageBlog loaded');
  },
});
</script>

<style lang="scss">
.blog {
  $self: &;
  @at-root #{$self}__header {
    padding-top: px_to_rem(25);
    padding-bottom: px_to_rem(25);
    @include mq($mq_sm_to_md) {
      padding-top: px_to_rem(45);
      padding-bottom: px_to_rem(45);
    }
    @at-root #{&}__breadcrumb {
      display: inline-block;
      color: $color_gray_112;
      margin-bottom: px_to_rem(25);
      &:hover,
      &:focus {
        text-decoration: none;
        color: $color_black;
      }
    }

    @at-root #{&}__header {
      @include font('schnyder_wide_m_bold');
      @include fs_lh(44, 54, 34, 40);
      padding-bottom: px_to_rem(25);
      max-width: 20ch;
    }
    @at-root #{&}__summary {
      @include font('graphik_regular');
      padding-bottom: px_to_rem(30);
    }
    @at-root #{&}__author_date {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: space-between;
      color: $color_gray_112;
      @include mq(500) {
        justify-content: flex-start;
      }
      @at-root #{&}__author {
        padding-right: px_to_rem(40);
      }
    }
  }
  @at-root #{$self}__social_share {
    padding-top: px_to_rem(25);
    @at-root #{&}__button {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      &:hover,
      &:focus {
        color: $color_red;
      }
      @at-root #{&}__text {
        padding-right: px_to_rem(5);
      }
    }
    @at-root #{&}__copy_to_clipboard {
      display: grid;
      grid-template-columns: 1fr px_to_rem(100);
      grid-gap: px_to_rem(5) px_to_rem(16);
      max-width: px_to_rem(400);
      font-size: px_to_rem(14);
      .c_copy_to_clipboard {
        @at-root #{&}__label {
          grid-column: 1/3;
          grid-row: 1;
        }
        @at-root #{&}__input {
          grid-column: 1;
          grid-row: 2;
        }
        @at-root #{&}__button {
          grid-column: 2;
          grid-row: 2;
        }
      }
    }
  }
  @at-root #{$self}__block_matrix_wrapper {
    padding-bottom: px_to_rem(50);
  }
  @at-root #{$self}__lp_matrix {
    padding-top: px_to_rem(50);
    max-width: var(--max-width);
    margin: 0 auto;
  }
}
</style>
